import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Settings, ClipboardList, Activity, Copy, Check, Info, X,
  Repeat, Flag, PartyPopper, Pencil, AlertTriangle, MinusCircle,
} from "lucide-react";

/* ------------------- module tab bar (Rules | Logs | Events) ------------------- */
// Mirrors src/components/diagnostic/primitives.jsx's DiagnosticTabs — same
// shape (icon + label, underline active state) so Customer Engagement reads
// as one consistent module family with Diagnostic Labs, not a one-off.
const ENGAGEMENT_TABS = [
  { key: "rules", label: "Rules", icon: Settings, to: "/subscriber-wallet/engagement" },
  { key: "logs", label: "Engagement Logs", icon: ClipboardList, to: "/subscriber-wallet/engagement-logs" },
  { key: "events", label: "Application Events", icon: Activity, to: "/subscriber-wallet/application-events" },
];

export const EngagementTabs = ({ active }) => {
  const navigate = useNavigate();
  return (
    <div className="border-b border-gray-200">
      <nav className="flex gap-1 -mb-px" aria-label="Customer Engagement sections">
        {ENGAGEMENT_TABS.map((t) => {
          const Icon = t.icon;
          const selected = active === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => !selected && navigate(t.to)}
              aria-current={selected ? "page" : undefined}
              className={`group inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors focus:outline-none ${
                selected
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300"
              }`}
            >
              <Icon className={`w-4 h-4 ${selected ? "text-blue-600" : "text-gray-400 group-hover:text-gray-600"}`} />
              {t.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

/* ------------------------------- stat card ------------------------------ */
// Identical to diagnostic/primitives.jsx's StatCard — kept as its own copy
// rather than a cross-module import so Customer Engagement doesn't take a
// dependency on the Diagnostics feature folder for a shared visual primitive.
// `compact` shrinks padding/icon/text so a 5-up row (Engagement Logs' KPI
// strip) doesn't wrap or feel cramped the way squeezing the default size
// into a 5-column grid would.
export const StatCard = ({ icon: Icon, label, value, accent, loading, index = 0, compact = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay: index * 0.06, ease: "easeOut" }}
    className={`relative overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow ${compact ? "p-3.5" : "p-5"}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className={`font-medium text-gray-500 uppercase tracking-wider ${compact ? "text-[10px]" : "text-xs"}`}>{label}</p>
        <div className={`flex items-end ${compact ? "mt-1 h-7" : "mt-2 h-9"}`}>
          {loading ? (
            <div className={`rounded-md bg-gray-100 animate-pulse ${compact ? "h-5 w-9" : "h-7 w-12"}`} />
          ) : (
            <span className={`font-bold text-gray-900 leading-none ${compact ? "text-xl" : "text-3xl"}`}>{value}</span>
          )}
        </div>
      </div>
      <div className={`rounded-xl flex items-center justify-center shrink-0 ${accent.bg} ${accent.fg} ${compact ? "w-8 h-8" : "w-11 h-11"}`}>
        <Icon className={compact ? "w-3.5 h-3.5" : "w-5 h-5"} />
      </div>
    </div>
    <div className={`absolute bottom-0 left-0 right-0 h-1 ${accent.bar}`} />
  </motion.div>
);

/* --------------------------- dotted status badge -------------------------- */
export const DotBadge = ({ label, colorClass, dotClass }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset ${colorClass}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
    {label}
  </span>
);

/* --------------------- copyable id (application_id, etc) ------------------ */
// Truncated text + copy-to-clipboard button — any value that can run long
// (ids, event names, journeys, emails, JSON values) gets cut to 15 chars
// with an ellipsis by default, with a copy button so the full value is
// still one click away. Shared by ApplicationEvents.jsx and
// EngagementMessageLogs.jsx.
export const CopyableId = ({ value, displayLength = 15, size = "sm" }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-gray-400">—</span>;

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard API unavailable — no-op, id is still visible to select manually
    }
  };

  const truncated = value.length > displayLength;
  const textSize = size === "md" ? "text-sm" : "text-xs";
  const iconSize = size === "md" ? "w-3.5 h-3.5" : "w-3 h-3";

  return (
    <button type="button" onClick={handleCopy} title={value}
      className={`inline-flex items-center gap-1.5 font-mono transition-colors ${textSize}`}>
      {truncated ? `${value.slice(0, displayLength)}…` : value}
      {copied ? <Check className={`${iconSize} text-emerald-500`} /> : <Copy className={iconSize} />}
    </button>
  );
};

/* -------------------- "what does Campaign mean?" help popup --------------- */
// Plain-English explanation of every EngagementSent.recurring_status value
// (cep/documents/engagement_sent.py) — written for a non-technical reader,
// not a copy of the backend docstrings. Opens from an (i) icon next to the
// "Campaign" column header.
const CAMPAIGN_STATUS_GUIDE = [
  {
    icon: Repeat,
    color: "text-blue-600",
    bg: "bg-blue-50",
    label: "Scheduled",
    what: "The reminder is on autopilot.",
    detail: "We've sent at least one reminder and we're waiting to send the next one automatically — no one needs to do anything.",
  },
  {
    icon: Flag,
    color: "text-slate-600",
    bg: "bg-slate-100",
    label: "Cap reached",
    what: "We've reminded the user as many times as we're allowed to.",
    detail: "This rule has a limit on how many reminders one user can get. We hit that limit, so we've stopped — even though the user hasn't taken the action yet.",
  },
  {
    icon: PartyPopper,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    label: "Converted",
    what: "It worked! The user did the thing we were reminding them about.",
    detail: "The moment the user completes the next step, we stop messaging them immediately — no more reminders needed.",
  },
  {
    icon: Pencil,
    color: "text-amber-600",
    bg: "bg-amber-50",
    label: "Rule changed",
    what: "An admin changed the rule while it was running.",
    detail: "An admin turned this reminder off, or edited the rule, partway through — so the remaining reminders for the user were cancelled.",
  },
  {
    icon: AlertTriangle,
    color: "text-red-600",
    bg: "bg-red-50",
    label: "Stopped (error)",
    what: "Something went wrong sending the message, so we gave up.",
    detail: "This is usually a broken WhatsApp template or an unreachable number — the same problem would happen every time, so we stop instead of retrying forever.",
  },
  {
    icon: MinusCircle,
    color: "text-gray-500",
    bg: "bg-gray-100",
    label: "Not recurring",
    what: "This reminder was only ever meant to be sent once.",
    detail: "Not every rule repeats — some are set up to send a single message and that's it, by design.",
  },
];

export const CampaignStatusHelp = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors align-middle ml-1"
        aria-label="What do these campaign statuses mean?">
        <Info className="w-3.5 h-3.5" />
      </button>
      {/* Portalled to document.body — this button can render inside a
          sticky <thead>/table-row context, and position:sticky ancestors
          turn `fixed` descendants into being positioned relative to the
          sticky element instead of the viewport in most browsers. A
          portal escapes that entirely so the overlay always covers the
          full screen (e.g. above the sticky filter bar), regardless of
          where CampaignStatusHelp is used from. */}
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4"
              onClick={() => setOpen(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 8 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">What does "Campaign" mean?</h3>
                    <p className="text-sm text-gray-500 mt-0.5">A quick guide to each status, in plain English.</p>
                  </div>
                  <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 shrink-0"><X className="w-5 h-5" /></button>
                </div>
                <div className="px-6 py-5 space-y-3">
                  {CAMPAIGN_STATUS_GUIDE.map((s) => {
                    const Icon = s.icon;
                    return (
                      <div key={s.label} className="flex gap-3 p-3 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
                        <div className={`flex items-center justify-center w-9 h-9 rounded-full shrink-0 ${s.bg} ${s.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{s.label}</p>
                          <p className="text-sm text-gray-700 mt-0.5">{s.what}</p>
                          <p className="text-xs text-gray-500 mt-1 leading-relaxed">{s.detail}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};
